<script setup>
import { shallowRef, watch } from 'vue';

// One settings panel: small heading + description, the form body, and a
// footer with Save (enabled when there are changes) and a brief "Saved.".
const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  dirty: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  savedAt: { type: Number, default: 0 },
  hideActions: { type: Boolean, default: false },
});
defineEmits(['save', 'reset']);

const showSaved = shallowRef(false);
let timer = null;
watch(
  () => props.savedAt,
  (at) => {
    if (!at) return;
    showSaved.value = true;
    clearTimeout(timer);
    timer = setTimeout(() => (showSaved.value = false), 2000);
  },
);
</script>

<template>
  <section class="section">
    <header class="section__head">
      <h3 class="section__title">{{ title }}</h3>
      <p v-if="description" class="section__description">{{ description }}</p>
    </header>

    <slot />

    <footer v-if="!hideActions" class="section__actions">
      <v-btn color="primary" :loading="saving" :disabled="!dirty" @click="$emit('save')">
        Save
      </v-btn>
      <v-btn v-if="dirty && !saving" variant="text" @click="$emit('reset')">
        Discard
      </v-btn>
      <transition name="fade">
        <span v-if="showSaved && !dirty" class="section__saved">Saved.</span>
      </transition>
    </footer>
  </section>
</template>

<style scoped>
.section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.section__head {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.section__title {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: var(--foreground);
}
.section__description {
  margin: 0;
  font-size: 14px;
  color: var(--muted-foreground);
}
.section__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
.section__saved {
  font-size: 14px;
  color: var(--muted-foreground);
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
